
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TrendingUp, Calendar, BarChart3, Target, Download, Filter } from "lucide-react";
import NavBar from "@/components/NavBar";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function ProgressReporting() {
  const [selectedPeriod, setSelectedPeriod] = useState("current-month");
  const [selectedProject, setSelectedProject] = useState("all");

  const progressData = {
    overall: 68,
    milestones: 85,
    budget: 72,
    timeline: 89,
    quality: 91
  };

  const milestones = [
    { name: "Project Initiation", status: "completed", progress: 100, dueDate: "2024-01-15" },
    { name: "Design Phase", status: "completed", progress: 100, dueDate: "2024-02-28" },
    { name: "Development Phase", status: "active", progress: 75, dueDate: "2024-04-30" },
    { name: "Testing Phase", status: "pending", progress: 0, dueDate: "2024-05-31" },
    { name: "Deployment", status: "pending", progress: 0, dueDate: "2024-06-15" }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'active': return 'bg-blue-100 text-blue-800';
      case 'at-risk': return 'bg-yellow-100 text-yellow-800';
      case 'delayed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <NavBar />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
            <TrendingUp className="h-8 w-8 text-primary" />
            Progress Reporting
          </h1>
          <p className="text-muted-foreground">
            Comprehensive project progress tracking and performance reporting
          </p>
        </div>

        <div className="flex gap-4 mb-6">
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="current-month">Current Month</SelectItem>
              <SelectItem value="last-month">Last Month</SelectItem>
              <SelectItem value="current-quarter">Current Quarter</SelectItem>
              <SelectItem value="last-quarter">Last Quarter</SelectItem>
              <SelectItem value="year-to-date">Year to Date</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={selectedProject} onValueChange={setSelectedProject}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Projects</SelectItem>
              <SelectItem value="project-1">New Office Building</SelectItem>
              <SelectItem value="project-2">IT System Upgrade</SelectItem>
              <SelectItem value="project-3">Manufacturing Equipment</SelectItem>
            </SelectContent>
          </Select>

          <Button variant="outline">
            <Filter className="h-4 w-4 mr-2" />
            Advanced Filters
          </Button>
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="milestones">Milestones</TabsTrigger>
            <TabsTrigger value="performance">Performance</TabsTrigger>
            <TabsTrigger value="reports">Reports</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">{progressData.overall}%</div>
                    <p className="text-sm text-muted-foreground">Overall Progress</p>
                    <Progress value={progressData.overall} className="mt-2" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{progressData.milestones}%</div>
                    <p className="text-sm text-muted-foreground">Milestones</p>
                    <Progress value={progressData.milestones} className="mt-2" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{progressData.budget}%</div>
                    <p className="text-sm text-muted-foreground">Budget Utilization</p>
                    <Progress value={progressData.budget} className="mt-2" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">{progressData.timeline}%</div>
                    <p className="text-sm text-muted-foreground">Timeline Adherence</p>
                    <Progress value={progressData.timeline} className="mt-2" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">{progressData.quality}%</div>
                    <p className="text-sm text-muted-foreground">Quality Score</p>
                    <Progress value={progressData.quality} className="mt-2" />
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    Key Performance Indicators
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Projects On Track:</span>
                      <Badge className="bg-green-100 text-green-800">8/10</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium">At Risk Projects:</span>
                      <Badge className="bg-yellow-100 text-yellow-800">2/10</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Delayed Projects:</span>
                      <Badge className="bg-red-100 text-red-800">0/10</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Average Completion:</span>
                      <span className="font-semibold">{progressData.overall}%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Budget Variance:</span>
                      <span className="font-semibold text-green-600">-2.3%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    Progress Trend
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span>This Month:</span>
                      <span className="font-semibold text-green-600">+12%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Last Month:</span>
                      <span className="font-semibold text-blue-600">+8%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>3 Months Ago:</span>
                      <span className="font-semibold text-purple-600">+15%</span>
                    </div>
                    <div className="pt-2 border-t">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">Average Monthly Progress:</span>
                        <span className="font-semibold">11.7%</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="milestones" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Project Milestones
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {milestones.map((milestone, index) => (
                    <Card key={index}>
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold">{milestone.name}</h4>
                            <p className="text-sm text-muted-foreground">
                              Due: {new Date(milestone.dueDate).toLocaleDateString()}
                            </p>
                            <div className="mt-2">
                              <div className="flex justify-between text-sm mb-1">
                                <span>Progress</span>
                                <span>{milestone.progress}%</span>
                              </div>
                              <Progress value={milestone.progress} className="w-full" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <Badge className={getStatusColor(milestone.status)}>
                              {milestone.status}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="performance" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Performance Metrics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span>Schedule Performance Index (SPI):</span>
                        <span className="font-semibold text-green-600">1.08</span>
                      </div>
                      <Progress value={108} className="w-full" />
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span>Cost Performance Index (CPI):</span>
                        <span className="font-semibold text-blue-600">0.97</span>
                      </div>
                      <Progress value={97} className="w-full" />
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span>Quality Performance Index:</span>
                        <span className="font-semibold text-purple-600">1.12</span>
                      </div>
                      <Progress value={112} className="w-full" />
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span>Resource Utilization:</span>
                        <span className="font-semibold text-orange-600">89%</span>
                      </div>
                      <Progress value={89} className="w-full" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Team Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span>Team Productivity:</span>
                      <Badge className="bg-green-100 text-green-800">High</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Task Completion Rate:</span>
                      <span className="font-semibold">94%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Average Task Velocity:</span>
                      <span className="font-semibold">23 points/sprint</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Burndown Rate:</span>
                      <span className="font-semibold text-green-600">On Track</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Team Satisfaction:</span>
                      <span className="font-semibold">4.2/5</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="reports" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Progress Reports</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Executive Summary</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Detailed Progress Report</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Milestone Analysis</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Performance Dashboard</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Team Performance Report</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Trend Analysis</div>
                    </div>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
